import { Injectable, inject } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  BillingProduct,
  normalizeProductName,
  parseProductPrice,
} from '../../models/billing-product.type';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { MembershipService } from '../auth/membership.service';

@Injectable({ providedIn: 'root' })
export class BillingProductService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private membership = inject(MembershipService);

  async listProducts(): Promise<BillingProduct[]> {
    const snap = await getDocs(this.collection());
    return snap.docs
      .map((productDoc) => parseBillingProduct(productDoc.id, productDoc.data()))
      .filter((product): product is BillingProduct => product != null)
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  }

  async createProduct(input: {
    name: string;
    price: number;
    priceEditable: boolean;
  }): Promise<string> {
    this.assertOwner();
    const name = requireName(input.name);
    const price = requirePrice(input.price);
    const productRef = doc(this.collection());
    await setDoc(productRef, {
      name,
      price,
      priceEditable: input.priceEditable === true,
    });
    return productRef.id;
  }

  async updateProduct(
    productId: string,
    input: { name: string; price: number; priceEditable: boolean }
  ): Promise<void> {
    this.assertOwner();
    await updateDoc(this.productDoc(productId), {
      name: requireName(input.name),
      price: requirePrice(input.price),
      priceEditable: input.priceEditable === true,
    });
  }

  async deleteProduct(productId: string): Promise<void> {
    this.assertOwner();
    await deleteDoc(this.productDoc(productId));
  }

  private assertOwner(): void {
    if (!this.membership.canManageSelectedBusiness()) {
      throw new Error('Solo el propietario o un administrador puede modificar los productos.');
    }
  }

  private collection() {
    const businessId = this.membership.requireSelectedBusinessId();
    return collection(this.firestore, 'businesses', businessId, 'billingProducts');
  }

  private productDoc(productId: string) {
    const businessId = this.membership.requireSelectedBusinessId();
    return doc(this.firestore, 'businesses', businessId, 'billingProducts', productId);
  }
}

function requireName(name: string): string {
  const normalized = normalizeProductName(name);
  if (!normalized) {
    throw new Error('El nombre del producto es obligatorio.');
  }
  return normalized;
}

function requirePrice(price: number): number {
  const parsed = parseProductPrice(price);
  if (parsed == null) {
    throw new Error('El precio no es válido.');
  }
  return parsed;
}

function parseBillingProduct(id: string, data: Record<string, unknown>): BillingProduct | null {
  const name = typeof data['name'] === 'string' ? normalizeProductName(data['name']) : '';
  const price = typeof data['price'] === 'number' ? parseProductPrice(data['price']) : null;
  if (!name || price == null) {
    return null;
  }
  return {
    id,
    name,
    price,
    priceEditable: data['priceEditable'] === true,
  };
}
